import { useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const DELTA_CONFIG = {
  REGRESSED: { icon: '🔴', label: 'REGRESSED', bg: '#fdecea', color: '#c0392b', border: '#e74c3c' },
  FIXED:     { icon: '🟢', label: 'FIXED',     bg: '#eafaf1', color: '#1e8449', border: '#27ae60' },
  NEW:       { icon: '🔵', label: 'NEW',        bg: '#eaf4fb', color: '#1a5276', border: '#2980b9' },
  UNCHANGED: { icon: '⚪', label: 'UNCHANGED',  bg: '#f9f9f9', color: '#666',    border: '#ddd'    },
};

export default function RegressionView() {
  const [currentJobId,  setCurrentJobId]  = useState('');
  const [baselineJobId, setBaselineJobId] = useState('');
  const [result,        setResult]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [filter,        setFilter]        = useState('ALL');

  const compare = async () => {
    if (!currentJobId.trim() || !baselineJobId.trim()) {
      setError('Both Job IDs are required.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res  = await fetch(`${API}/api/regression/${currentJobId.trim()}?baseline=${baselineJobId.trim()}&t=${Date.now()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const filtered = result
    ? (filter === 'ALL' ? result.rulesDiff : result.rulesDiff.filter(r => r.delta === filter))
    : [];

  return (
    <div style={s.page}>
      <h2 style={s.heading}>📈 Regression Comparison</h2>
      <p style={s.sub}>Compare two design versions rule-by-rule to identify fixes and regressions.</p>

      {/* ── Input ── */}
      <div style={s.card}>
        <div style={s.row}>
          <div style={s.field}>
            <label style={s.label}>Current Job ID (new version)</label>
            <input style={s.input} value={currentJobId}
              onChange={e => setCurrentJobId(e.target.value)}
              placeholder="e.g. abc-002" />
          </div>
          <div style={s.field}>
            <label style={s.label}>Baseline Job ID (previous version)</label>
            <input style={s.input} value={baselineJobId}
              onChange={e => setBaselineJobId(e.target.value)}
              placeholder="e.g. abc-001" />
          </div>
          <button style={s.btn} onClick={compare} disabled={loading}>
            {loading ? 'Comparing…' : 'Compare →'}
          </button>
        </div>
        {error && <p style={s.error}>{error}</p>}
      </div>

      {/* ── Results ── */}
      {result && (
        <>
          {/* Version header */}
          <div style={s.versionBar}>
            <div style={s.versionBox}>
              <span style={s.vLabel}>Baseline</span>
              <span style={s.vName}>{result.baselineVersion}</span>
              <span style={s.vScore}>{result.baselineScore}%</span>
            </div>
            <span style={s.arrow}>→</span>
            <div style={s.versionBox}>
              <span style={s.vLabel}>Current</span>
              <span style={s.vName}>{result.currentVersion}</span>
              <span style={s.vScore}>{result.currentScore}%</span>
            </div>
          </div>

          {/* Summary banner */}
          <div style={s.summaryRow}>
            {[
              ['FIXED',     result.summary.fixed,     '🟢'],
              ['REGRESSED', result.summary.regressed,  '🔴'],
              ['NEW',       result.summary.new,        '🔵'],
              ['UNCHANGED', result.summary.unchanged,  '⚪'],
            ].map(([key, count, icon]) => (
              <div key={key} style={{
                ...s.summaryCard,
                background: DELTA_CONFIG[key].bg,
                border: `1px solid ${DELTA_CONFIG[key].border}`,
                cursor: 'pointer',
                outline: filter === key ? `2px solid ${DELTA_CONFIG[key].border}` : 'none',
              }} onClick={() => setFilter(filter === key ? 'ALL' : key)}>
                <span style={{ fontSize: 22 }}>{icon}</span>
                <span style={{ fontSize: 24, fontWeight: 700, color: DELTA_CONFIG[key].color }}>{count}</span>
                <span style={{ fontSize: 12, color: '#555' }}>{key}</span>
              </div>
            ))}
            <div style={{ ...s.summaryCard, cursor: 'pointer', outline: filter === 'ALL' ? '2px solid #0073bb' : 'none' }}
              onClick={() => setFilter('ALL')}>
              <span style={{ fontSize: 22 }}>📋</span>
              <span style={{ fontSize: 24, fontWeight: 700 }}>{result.rulesDiff.length}</span>
              <span style={{ fontSize: 12, color: '#555' }}>ALL RULES</span>
            </div>
          </div>

          {/* Rule diff table */}
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Rule ID</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>Threshold</th>
                  <th style={s.th}>Baseline</th>
                  <th style={s.th}>Baseline Value</th>
                  <th style={s.th}>Current</th>
                  <th style={s.th}>Current Value</th>
                  <th style={s.th}>Delta</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(rule => {
                  const cfg = DELTA_CONFIG[rule.delta] || DELTA_CONFIG.UNCHANGED;
                  return (
                    <tr key={rule.ruleName} style={{ background: cfg.bg }}>
                      <td style={{ ...s.td, fontWeight: 600, fontFamily: 'monospace' }}>{rule.ruleName}</td>
                      <td style={s.td}>{rule.category}</td>
                      <td style={{ ...s.td, fontSize: 12, color: '#666' }}>{rule.threshold || '—'}</td>
                      <td style={s.td}>{_verdict(rule.baselinePassed)}</td>
                      <td style={{ ...s.td, fontSize: 12, color: '#555', maxWidth: 160, wordBreak: 'break-word' }}>
                        {rule.baselineActual || '—'}
                      </td>
                      <td style={s.td}>{_verdict(rule.currentPassed)}</td>
                      <td style={{ ...s.td, fontSize: 12, color: '#555', maxWidth: 160, wordBreak: 'break-word' }}>
                        {rule.currentActual || '—'}
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, background: cfg.border, color: '#fff' }}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No rules match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function _verdict(passed) {
  if (passed === null || passed === undefined) return <span style={{ color: '#aaa' }}>—</span>;
  return passed
    ? <span style={{ color: '#27ae60', fontWeight: 600 }}>✅ PASS</span>
    : <span style={{ color: '#c0392b', fontWeight: 600 }}>❌ FAIL</span>;
}

const s = {
  page:        { maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif' },
  heading:     { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub:         { color: '#666', marginBottom: 20, fontSize: 14 },
  card:        { background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', marginBottom: 20 },
  row:         { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
  field:       { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 200 },
  label:       { fontSize: 13, fontWeight: 600, color: '#333' },
  input:       { padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 },
  btn:         { padding: '9px 24px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' },
  error:       { color: '#c0392b', marginTop: 8, fontSize: 13 },
  versionBar:  { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  versionBox:  { background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '12px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 120 },
  vLabel:      { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 },
  vName:       { fontSize: 18, fontWeight: 700, color: '#0073bb' },
  vScore:      { fontSize: 13, color: '#555' },
  arrow:       { fontSize: 24, color: '#aaa' },
  summaryRow:  { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  summaryCard: { background: '#fff', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead:       { background: '#f0f4f8' },
  th:          { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' },
  td:          { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' },
  badge:       { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
};
